import { clearAccessToken, setAccessToken } from './accessTokenStore';
import { resolveBaseUrl } from '@/api/baseUrl';
import type { components } from '@/api/schema';

/** The signed-in user exactly as the server describes them. */
export type AuthenticatedUser = components['schemas']['AuthenticatedUser'];

/** A session response from the login or refresh endpoint. */
export type AuthenticationResponse = components['schemas']['AuthenticationResponse'];

/**
 * Who the session currently belongs to, and who is listening for that to change.
 *
 * The access token alone cannot answer "is this still the same person?". Two tokens are
 * indistinguishable to anything holding one, so a refresh that returns a DIFFERENT user — another
 * tab signed in as somebody else against the same cookie — used to be invisible: the identity was
 * thrown away and only the token was kept. Publishing the user makes that switch observable, which
 * is what lets the interface re-resolve and the transport refuse to replay a request across it.
 */
let currentUser: AuthenticatedUser | null = null;
const sessionListeners = new Set<
  (user: AuthenticatedUser | null, accessChanged: boolean) => void
>();

/**
 * Bumped whenever the session is deliberately replaced — signed in, or signed out.
 *
 * An exchange that started before the bump belongs to a session that no longer exists. Without
 * this, signing out while a refresh was already in flight let that refresh install its token and
 * publish its user a moment later, and the interface came back signed in after the user had signed
 * out. The generation is captured when the exchange starts and checked before anything it returns
 * is allowed to take effect.
 */
let sessionGeneration = 0;

/** Name of the cross-tab lock and channel; same origin, so both are private to this application. */
const REFRESH_COORDINATION = 'fieldops-refresh';

/**
 * How long a sibling tab's freshly minted session stays adoptable.
 *
 * Short, because it only has to cover the moment two tabs mount at once. Adopting anything older
 * would mean running on a token whose remaining lifetime is unknown.
 */
const ADOPTION_WINDOW_MS = 10_000;

interface BroadcastSession {
  readonly kind: 'session';
  readonly source: 'login' | 'refresh';
  readonly user: AuthenticatedUser;
  readonly accessToken: string;
  readonly at: number;
}

interface BroadcastSignOut {
  readonly kind: 'signed-out';
  readonly at: number;
}

type BroadcastAuth = BroadcastSession | BroadcastSignOut;

/**
 * When this tab last signed out.
 *
 * A sibling's refresh can be in flight while this tab signs out, and its result arrives afterwards.
 * Installing it would make the tab authenticated again moments after the user signed out, which is
 * the same defect as the single-tab race but arriving down a different pipe. Messages minted before
 * the sign-out are therefore ignored.
 */
let signedOutAt = 0;

let adoptable: BroadcastSession | null = null;

/** Whether the session ended because somebody chose to sign out, rather than by expiring. */
let endedBySignOut = false;

/** A Lamport-style local stamp keeps delayed messages from putting an older session back. */
let lastAuthStamp = 0;

function nextAuthStamp(): number {
  lastAuthStamp = Math.max(Date.now(), lastAuthStamp + 1);
  return lastAuthStamp;
}

function acceptAuthStamp(at: number): boolean {
  if (!Number.isFinite(at) || at <= lastAuthStamp) {
    return false;
  }
  lastAuthStamp = at;
  return true;
}

/**
 * The channel siblings use to hand each other a freshly minted session.
 *
 * The refresh cookie rotates on every exchange, so only ONE tab may exchange it: a second tab
 * presenting the same cookie looks exactly like a stolen credential, and the server correctly
 * revokes the family — normal browsing would sign the user out. The winner therefore broadcasts
 * what it got, and the others adopt it instead of exchanging again.
 *
 * The token travels in memory over a same-origin channel and is never persisted, which keeps the
 * invariant accessTokenStore exists to protect: nothing readable by a later page load, and nothing
 * for injected script to find in storage.
 */
const channel: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(REFRESH_COORDINATION);

channel?.addEventListener('message', (event: MessageEvent<BroadcastAuth>) => {
  // Typed as possibly absent on purpose: the DOM types promise a message shape, but anything
  // same-origin can post to this channel and a malformed message must not install a token.
  const message = event.data as BroadcastAuth | null | undefined;
  if (message === null || message === undefined || typeof message !== 'object') {
    return;
  }

  if (message.kind === 'signed-out') {
    if (!acceptAuthStamp(message.at)) {
      return;
    }
    // Signing out is a decision about the session, and the session is shared by every tab holding
    // the same cookie. A tab that kept its token would go on making authorised requests after the
    // user believed they had left.
    signedOutAt = message.at;
    endedBySignOut = true;
    adoptable = null;
    sessionGeneration += 1;
    clearAccessToken();
    publishSession(null);
    return;
  }

  const source = (message as { source?: unknown }).source;
  const user = (message as { user?: AuthenticatedUser | null }).user;
  if (
    typeof message.accessToken !== 'string' ||
    (source !== 'login' && source !== 'refresh') ||
    message.at <= signedOutAt ||
    user === null ||
    typeof user !== 'object' ||
    !Array.isArray((user as Partial<AuthenticatedUser>).roles) ||
    !Array.isArray((user as Partial<AuthenticatedUser>).permissions)
  ) {
    // Minted before this tab signed out: it belongs to a session that has ended.
    return;
  }
  // A refresh from a previous principal can still be queued in the channel after a deliberate
  // sign-in. The explicit login message is the replacement boundary; do not let that old token
  // overwrite the principal that is already visible here. A tab that explicitly signed out also
  // waits for a login message before accepting another refresh, while a first-load tab with no
  // local sign-out decision may adopt a sibling's refresh and avoid presenting the rotating cookie
  // twice.
  if (
    source === 'refresh' &&
    ((currentUser === null && signedOutAt > 0) ||
      (currentUser !== null && sessionIdentity(currentUser) !== sessionIdentity(message.user)))
  ) {
    return;
  }
  if (!acceptAuthStamp(message.at)) {
    return;
  }
  if (source === 'login') {
    sessionGeneration += 1;
  }
  adoptable = message;
  // A sibling refreshed; adopt its session so this tab stops holding a token that was rotated away.
  setAccessToken(message.accessToken);
  publishSession(message.user);
});

/**
 * Runs the exchange under a cross-tab lock when the browser has one.
 *
 * Web Locks is exactly the primitive this needs and is the only one that spans tabs; without it
 * (older browsers, and the test environment) the module-level promise still serialises within a
 * tab, which is where most of the races are.
 */
async function withRefreshLock<T>(work: () => Promise<T>): Promise<T> {
  // The DOM types declare navigator.locks as always present; older browsers and the test
  // environment disagree, so it is read as possibly absent rather than assumed.
  const locks: LockManager | undefined = getOptionalLocks();
  if (locks === undefined) {
    return fallbackAuthLock(work);
  }
  return locks.request(REFRESH_COORDINATION, work);
}

function getOptionalLocks(): LockManager | undefined {
  return globalThis.navigator.locks;
}

/** Serialises auth-cookie operations in browsers without Web Locks as well. */
let fallbackAuthQueue: Promise<void> = Promise.resolve();
let fallbackAuthBusy = false;

function fallbackAuthLock<T>(work: () => Promise<T>): Promise<T> {
  // Start the first operation synchronously. Apart from avoiding an unnecessary turn of the event
  // loop, this lets a caller attach a resolver to its fetch before it yields back to the browser.
  // Later operations wait on the same settled chain, which still serialises cookie rotation.
  if (!fallbackAuthBusy) {
    fallbackAuthBusy = true;
    let result: Promise<T>;
    try {
      result = work();
    } catch (error) {
      fallbackAuthBusy = false;
      throw error;
    }
    const done = result.then(
      () => undefined,
      () => undefined,
    );
    fallbackAuthQueue = done;
    done.then(
      () => {
        if (fallbackAuthQueue === done) fallbackAuthBusy = false;
      },
      () => {
        if (fallbackAuthQueue === done) fallbackAuthBusy = false;
      },
    );
    return result;
  }

  const result = fallbackAuthQueue.then(work);
  const done = result.then(
    () => undefined,
    () => undefined,
  );
  fallbackAuthQueue = done;
  done.then(
    () => {
      if (fallbackAuthQueue === done) fallbackAuthBusy = false;
    },
    () => {
      if (fallbackAuthQueue === done) fallbackAuthBusy = false;
    },
  );
  return result;
}

/**
 * A stable string for "which principal is this", or null when signed out.
 *
 * @param user the session user
 * @returns user and workspace, which together identify the acting principal
 */
export function sessionIdentity(user: AuthenticatedUser | null): string | null {
  return user === null ? null : `${user.id}@${user.tenantId}`;
}

/** Where the last signed-in principal is remembered for an offline start. */
const SESSION_HINT_KEY = 'fieldops-session-hint';

/**
 * Remembers who is signed in, so a reload with no signal can still open field mode.
 *
 * Only what the interface needs to render and to find this person's queued work: no token, no
 * email, nothing the server would accept as a credential. Cleared whenever the session ends for a
 * reason the server or the user gave, never because the network was down.
 */
function rememberSessionHint(user: AuthenticatedUser | null): void {
  try {
    if (user === null) {
      localStorage.removeItem(SESSION_HINT_KEY);
      return;
    }
    const hint: AuthenticatedUser = {
      id: user.id,
      tenantId: user.tenantId,
      displayName: user.displayName,
      workspaceName: user.workspaceName,
      workspaceSlug: user.workspaceSlug,
      workspaceCurrency: user.workspaceCurrency,
      workspaceTimeZone: user.workspaceTimeZone,
      roles: user.roles,
      permissions: user.permissions,
      platformAdmin: user.platformAdmin,
    };
    localStorage.setItem(SESSION_HINT_KEY, JSON.stringify(hint));
  } catch {
    // Storage blocked or full: an offline start is a convenience, the session itself is unaffected.
  }
}

/**
 * The principal who was signed in when the server was last reachable.
 *
 * Grants nothing: every request still needs a token the server issues. It only lets the interface
 * show field mode's queued work while the server cannot be asked who the user is.
 *
 * @returns the remembered principal, or null when there is none or it is unreadable
 */
export function readSessionHint(): AuthenticatedUser | null {
  try {
    const raw = localStorage.getItem(SESSION_HINT_KEY);
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed === null ||
      typeof parsed !== 'object' ||
      !('id' in parsed) ||
      typeof parsed.id !== 'string' ||
      !('tenantId' in parsed) ||
      typeof parsed.tenantId !== 'string' ||
      !('permissions' in parsed) ||
      !Array.isArray(parsed.permissions)
    ) {
      return null;
    }
    return parsed as AuthenticatedUser;
  } catch {
    return null;
  }
}

/**
 * The user the session currently belongs to.
 *
 * @returns the signed-in user, or null
 */
export function getSessionUser(): AuthenticatedUser | null {
  return currentUser;
}

/**
 * Whether nobody is signed in because somebody deliberately signed out, in this tab or another.
 *
 * A session that expired should send its user back to where they were once they sign in again; a
 * session somebody ended should not hand the next person to sign in the previous one's record.
 *
 * @returns true after a sign-out, until the next session begins
 */
export function sessionEndedBySignOut(): boolean {
  return currentUser === null && endedBySignOut;
}

/**
 * Subscribes to session identity changes.
 *
 * @param listener called whenever the session's user changes, including on sign-out
 * @returns a function that removes the subscription
 */
export function subscribeToSession(
  listener: (user: AuthenticatedUser | null, accessChanged: boolean) => void,
): () => void {
  sessionListeners.add(listener);
  return () => {
    sessionListeners.delete(listener);
  };
}

/**
 * What the interface must re-render for: who the caller is, and what they may do.
 *
 * Identity alone is not enough. An owner demoted to technician keeps the same user and workspace
 * ids, so a refresh that returned the reduced permission set changed nothing observable and the
 * screen went on offering the audit route it had already rendered. Authority belongs in the
 * fingerprint for the same reason identity does.
 */
function sessionAccessFingerprint(user: AuthenticatedUser | null): string | null {
  if (user === null) {
    return null;
  }
  const roles = [...user.roles].sort();
  const permissions = [...user.permissions].sort();
  return JSON.stringify([sessionIdentity(user), roles, permissions, user.platformAdmin]);
}

function sessionPresentationFingerprint(user: AuthenticatedUser | null): string | null {
  if (user === null) {
    return null;
  }
  return JSON.stringify([
    user.displayName,
    user.username ?? null,
    user.email ?? null,
    user.workspaceName,
    user.workspaceSlug,
  ]);
}

function publishSession(user: AuthenticatedUser | null): void {
  if (user !== null) {
    endedBySignOut = false;
  }
  const accessChanged = sessionAccessFingerprint(user) !== sessionAccessFingerprint(currentUser);
  const changed =
    accessChanged ||
    sessionPresentationFingerprint(user) !== sessionPresentationFingerprint(currentUser);
  currentUser = user;
  rememberSessionHint(user);
  if (!changed) {
    return;
  }
  for (const listener of sessionListeners) {
    listener(user, accessChanged);
  }
}

/** Reason a sign-in attempt was not accepted. */
export type LoginFailureKind =
  'INVALID_CREDENTIALS' | 'WORKSPACE_REQUIRED' | 'LOCKED_OUT' | 'UNAVAILABLE';

/** Thrown when the server refuses a sign-in. */
export class LoginFailedError extends Error {
  readonly kind: LoginFailureKind;

  constructor(kind: LoginFailureKind, message: string) {
    super(message);
    this.name = 'LoginFailedError';
    this.kind = kind;
  }
}

interface ProblemDetail {
  readonly detail?: string;
  readonly reason?: string;
}

/**
 * The authentication endpoints are called with the bare `fetch` rather than through the typed
 * client, because the typed client is the thing that retries on 401 by calling refresh. Routing
 * refresh through it would let a failing refresh trigger another refresh, indefinitely.
 */
async function postToAuth(path: string, body?: unknown): Promise<Response> {
  const init: RequestInit = {
    method: 'POST',
    // The refresh credential is an HttpOnly cookie; it has to be attached for these calls.
    credentials: 'include',
  };
  if (body !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  return fetch(`${resolveBaseUrl()}${path}`, init);
}

async function readProblem(response: Response): Promise<ProblemDetail> {
  try {
    return (await response.json()) as ProblemDetail;
  } catch {
    return {};
  }
}

/**
 * Exchanges credentials for a session.
 *
 * @param email login address
 * @param password plaintext password, held only for this call
 * @param workspace workspace slug, needed only when the address spans several workspaces
 * @returns the signed-in user
 * @throws LoginFailedError when the server refuses the credentials
 */
export async function login(
  email: string,
  password: string,
  workspace?: string,
): Promise<AuthenticatedUser> {
  // A deliberate sign-in must remain responsive while a startup refresh is stalled. Incrementing
  // the generation before the request makes any refresh already in flight harmless; the successful
  // response increments it again so a refresh that starts during this request is harmless too.
  const loginGeneration = ++sessionGeneration;
  adoptable = null;
  const response = await postToAuth('/api/auth/login', {
    email,
    password,
    ...(workspace !== undefined && workspace !== '' ? { workspace } : {}),
  });

  if (!response.ok) {
    const problem = await readProblem(response);
    if (problem.reason === 'WORKSPACE_REQUIRED') {
      throw new LoginFailedError(
        'WORKSPACE_REQUIRED',
        problem.detail ??
          'This address belongs to more than one workspace. Name the one you are signing in to.',
      );
    }
    if (response.status === 401 || response.status === 400) {
      throw new LoginFailedError(
        'INVALID_CREDENTIALS',
        problem.detail ?? 'Email address or password is incorrect.',
      );
    }
    if (response.status === 429) {
      const retryAfter = response.headers.get('retry-after');
      const wait =
        retryAfter && /^\d+$/.test(retryAfter)
          ? ` Try again in about ${String(Math.ceil(Number(retryAfter) / 60))} minute(s).`
          : '';
      throw new LoginFailedError(
        'LOCKED_OUT',
        (problem.detail ?? 'Too many sign-in attempts. Wait before trying again.') +
          (problem.detail ? '' : wait),
      );
    }
    throw new LoginFailedError(
      'UNAVAILABLE',
      'Sign-in is temporarily unavailable. Try again in a moment.',
    );
  }

  const session = (await response.json()) as AuthenticationResponse;
  if (loginGeneration !== sessionGeneration) {
    throw new LoginFailedError('UNAVAILABLE', 'Sign-in was superseded by another session change.');
  }
  // A deliberate sign-in replaces whatever session was here, including one an in-flight refresh
  // is still trying to resume.
  sessionGeneration += 1;
  const at = nextAuthStamp();
  setAccessToken(session.accessToken);
  publishSession(session.user);
  channel?.postMessage({
    kind: 'session',
    source: 'login',
    user: session.user,
    accessToken: session.accessToken,
    at,
  } satisfies BroadcastSession);
  return session.user;
}

/** What an attempt to resume the session came to. */
export type RefreshOutcome =
  | { readonly kind: 'resumed'; readonly user: AuthenticatedUser }
  | { readonly kind: 'ended' }
  | { readonly kind: 'unreachable' };

const ENDED: RefreshOutcome = { kind: 'ended' };
const UNREACHABLE: RefreshOutcome = { kind: 'unreachable' };

/** The refresh endpoint's answers that mean the cookie itself is missing, expired or revoked. */
const REFRESH_REFUSED = new Set([400, 401, 403]);

/**
 * Exchanges the refresh cookie for a new access token.
 *
 * Used both to recover a session after a page reload and to recover from an expired access token
 * mid-request. A failure is not an error condition — an anonymous visitor has no cookie — so this
 * reports it as an outcome rather than by throwing.
 *
 * @returns the resumed user, `ended` when there is no session, or `unreachable`
 */
async function exchangeRefreshCookie(generation: number): Promise<RefreshOutcome> {
  // Capture the generation before entering the cross-tab fallback queue. Without this early
  // check, a refresh requested just before sign-out could begin after the queue yields and adopt
  // the response as if it had been requested by the new session.
  if (generation !== sessionGeneration) {
    return ENDED;
  }

  // A sibling tab may have just done this. The cookie it rotated is the one this tab would
  // present, so exchanging again would be a duplicate presentation and the server would revoke
  // the family.
  if (adoptable !== null && Date.now() - adoptable.at < ADOPTION_WINDOW_MS) {
    const adopted = adoptable;
    setAccessToken(adopted.accessToken);
    publishSession(adopted.user);
    return { kind: 'resumed', user: adopted.user };
  }

  // Only the server saying the cookie is no good ends the session. No signal in a basement, or a
  // 502 while the backend is being deployed, says nothing about the credential; signing everybody
  // out for it would lose a technician's offline work and every office user's open form.
  let response: Response;
  try {
    response = await postToAuth('/api/auth/refresh');
  } catch {
    return UNREACHABLE;
  }

  if (REFRESH_REFUSED.has(response.status)) {
    return abandon(generation);
  }
  if (!response.ok) {
    return UNREACHABLE;
  }

  let session: AuthenticationResponse;
  try {
    session = (await response.json()) as AuthenticationResponse;
  } catch {
    // A captive portal or proxy answering 200 with its own page.
    return UNREACHABLE;
  }
  if (generation !== sessionGeneration) {
    // Signed out, or signed in as somebody else, while this was in flight. The token it just
    // minted belongs to a session nobody is in any more; installing it would undo the sign-out.
    return ENDED;
  }
  const at = nextAuthStamp();
  setAccessToken(session.accessToken);
  publishSession(session.user);
  // Broadcast, but do NOT mark it adoptable here. A session is only adoptable when it came from a
  // DIFFERENT tab; treating our own exchange as adoptable made the next refresh in this tab reuse
  // the token it had just minted instead of exchanging, which is not a refresh at all.
  channel?.postMessage({
    kind: 'session',
    source: 'refresh',
    user: session.user,
    accessToken: session.accessToken,
    at,
  } satisfies BroadcastSession);
  return { kind: 'resumed', user: session.user };
}

/** Gives up on an exchange, without disturbing a session that replaced it meanwhile. */
function abandon(generation: number): RefreshOutcome {
  if (generation !== sessionGeneration) {
    return ENDED;
  }
  endedBySignOut = false;
  clearAccessToken();
  publishSession(null);
  return ENDED;
}

/**
 * In-flight refresh, shared by every caller.
 *
 * Refresh rotates the cookie, so two concurrent exchanges of the same cookie are indistinguishable
 * from a stolen credential being replayed — the server revokes the whole family and signs the user
 * out for doing nothing. The transport coordinated its own retries, but the provider's mount-time
 * resume called straight through, and React's StrictMode runs that effect twice: an ordinary
 * reload in development could therefore revoke its own session. One promise here covers every
 * caller, which is the only way this stays true as new callers appear.
 */
let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * Exchanges the refresh cookie, saying why when no user comes back.
 *
 * @returns the resumed user; `ended` when there is no session; `unreachable` when the server could
 *   not be asked, in which case whatever session this tab held is left exactly as it was
 */
export function refreshSessionOutcome(): Promise<RefreshOutcome> {
  const generation = sessionGeneration;
  refreshInFlight ??= withRefreshLock(() => exchangeRefreshCookie(generation)).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/**
 * Exchanges the refresh cookie.
 *
 * @returns the signed-in user, or null when no fresh session came back — including when the server
 *   was unreachable, so a caller retrying a request answers with the original response
 */
export async function refreshSession(): Promise<AuthenticatedUser | null> {
  const outcome = await refreshSessionOutcome();
  return outcome.kind === 'resumed' ? outcome.user : null;
}

/**
 * Ends the session.
 *
 * The local token is cleared whatever the server says. A network failure must not leave the
 * interface showing a signed-in user who believes they have signed out.
 */
export async function logout(): Promise<void> {
  // Bump before the request, so an exchange already in flight in this tab cannot install its result
  // after the user has chosen to leave. Logout deliberately does not wait for the refresh lock:
  // the local session must be cleared even while a refresh request is stalled, and the generation
  // check below makes that in-flight result harmless.
  sessionGeneration += 1;
  try {
    await postToAuth('/api/auth/logout');
  } catch {
    // Intentionally ignored; see above.
  } finally {
    const at = nextAuthStamp();
    signedOutAt = at;
    endedBySignOut = true;
    adoptable = null;
    clearAccessToken();
    publishSession(null);
    // Every tab sharing this cookie is now signed out, whether or not it asked to be.
    channel?.postMessage({ kind: 'signed-out', at } satisfies BroadcastSignOut);
  }
}
